import type { ReactNode } from 'react';
import { AlertDialog as AD, Dialog as D } from 'radix-ui';
import { CloseButton } from './CloseButton';

const overlay = 'fixed inset-0 z-40 bg-black/30';
/** The window itself does not scroll: its title stays, the content below scrolls. */
const panel =
  'nx-pop-in fixed inset-x-0 top-[8vh] z-50 mx-auto flex max-h-[84vh] w-[min(34rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-bg shadow-pop';
const confirmPanel = `${panel} p-6`;
const widePanel = panel.replace(
  'w-[min(34rem,calc(100vw-2rem))]',
  'w-[min(56rem,calc(100vw-2rem))]',
);

export function Dialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <D.Root open={props.open} onOpenChange={props.onOpenChange}>
      <D.Portal>
        <D.Overlay className={overlay} />
        <D.Content className={props.wide ? widePanel : panel} aria-describedby={undefined}>
          <div className="flex shrink-0 items-start gap-3 px-6 pt-6 pb-4">
            <D.Title className="min-w-0 flex-1 text-lg font-semibold tracking-tight">
              {props.title}
            </D.Title>
            <D.Close asChild>
              <CloseButton className="-mt-1 -mr-2" />
            </D.Close>
          </div>
          {/* Scrollbar kept clear of the rounded edge and corners. */}
          <div className="mr-1.5 mb-2 min-h-0 flex-1 overflow-y-auto pr-4 pb-4 pl-6">
            {props.children}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Confirmation for destructive actions (window.confirm is unreliable inside Tauri webviews). */
export function ConfirmDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
}) {
  return (
    <AD.Root open={props.open} onOpenChange={props.onOpenChange}>
      <AD.Portal>
        <AD.Overlay className={overlay} />
        <AD.Content className={confirmPanel}>
          <AD.Title className="text-lg font-semibold">{props.title}</AD.Title>
          <AD.Description className="mt-2 text-sm text-fg-muted">
            {props.description}
          </AD.Description>
          <div className="mt-5 flex justify-end gap-2">
            <AD.Cancel className="rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover">
              {props.cancelLabel}
            </AD.Cancel>
            <AD.Action
              onClick={props.onConfirm}
              className="rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-white dark:text-black"
            >
              {props.confirmLabel}
            </AD.Action>
          </div>
        </AD.Content>
      </AD.Portal>
    </AD.Root>
  );
}
